import { useState } from 'react';
import { Fragment } from 'react/jsx-runtime';
import { useTranslation } from 'react-i18next';
import { useUIConfig } from '@cx-web/ui-shell/ui-config-context';
import { getRepubMetaFromRepub } from '@cx-web/utils';
import { type DehydratedState } from '@tanstack/react-query';

import { OddsGrid } from '~/modules';
import { type ComponentLoaderContext } from '~/types/component-loader-context';
import { type GridGroupComponentProps } from '~/types/template';
import { invalidDehydratedState } from '~/utils/invalidDehydratedState.ts';

import Filters from './Filters';
import { GridGroupOffers } from './GridGroupOffers';
import { useMarkets, useTabs } from './hooks';
import Tabs, { type TabName } from './Tabs';

export const GridGroup = ({
  componentArgs: {
    DISABLE_GRID_TAB_STATS,
    DISABLE_OFFER_WIDGETS,
    bookmakers,
    gridOfferData,
    gridOffers,
    marketGroups,
    oddsGrids,
    oddsShown,
    overrideBookies,
    overrideMarketIds,
    projectionsUrl,
    selectedMarketId: initialSelectedMarketId,
    tabbedOffers,
    tabbedOffersData,
  },
  clickoutUrl,
  currentPathWithRepub,
  deviceType,
  geoLocationInfo,
  gtmId,
  oddsFormat,
  repub,
  requestUrl,
}: GridGroupComponentProps &
  ComponentLoaderContext & {
    // NGF-6128: gtmId is no longer part of the component context; it is merged onto the props by
    // renderComponents (threaded from the loader response) for the offers clickout config below.
    gtmId: string;
  }) => {
  // NGF-6268: `oddsFormat` is the server-rendered value from ComponentLoaderContext (no betslip-context
  // coupling, for Astro island portability). This is an accepted trade-off: an in-session odds-format
  // change now only takes effect on the next full-page navigation, not reactively in place.
  const { resourcesUrl } = useUIConfig();
  const isOC = getRepubMetaFromRepub(repub).repubIs.OC;

  const { i18n, t } = useTranslation('label');
  const OFFERS = t('CX_OFFERS') as TabName;
  const BEST_ODDS = t('CX_BEST_ODDS') as TabName;

  const { handleTabChange, selectedTab, tabElements } = useTabs({
    DISABLE_GRID_TAB_STATS,
    projectionsUrl,
    repub,
  });

  const [selectedMarketId, setSelectedMarketId] = useState<number | null>(initialSelectedMarketId ?? null);

  const { handleMarketChange, marketGroupNames, selectedMarketGroup } = useMarkets(
    marketGroups,
    selectedMarketId,
    setSelectedMarketId,
  );

  // NGF-6268: GridGroupOffers must inject the offers-clickout props (the modules no longer read them
  // from provider context). These request-scoped values come straight from ComponentLoaderContext
  // (gtmId is now a sibling prop merged by renderComponents from the loader response).
  const offersClickoutConfig = {
    clickoutUrl,
    currentPathWithRepub,
    deviceType,
    fullPageUrl: requestUrl,
    gtmId,
  };

  const renderGridOffers = () => {
    return gridOffers ? (
      <GridGroupOffers {...gridOffers} {...offersClickoutConfig} className="grid" dehydratedState={gridOfferData} />
    ) : null;
  };

  const renderSubeventMarkets = () => {
    if (!oddsGrids || !marketGroups[selectedMarketGroup]) return null;

    const activeSubeventMarkets = marketGroups[selectedMarketGroup];

    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}
      >
        {activeSubeventMarkets
          .filter(market => oddsGrids[market.ocMarketId])
          .map((market, idx) => {
            const dehydratedState = oddsGrids[market.ocMarketId];
            if (invalidDehydratedState(dehydratedState)) return null;
            return (
              <Fragment key={`${market.marketDisplayName}-${selectedMarketGroup}`}>
                <OddsGrid
                  bestOddsStartExpanded={selectedMarketId ? selectedMarketId === market.ocMarketId : true}
                  // @ts-ignore
                  bookmakersConfig={bookmakers}
                  countryCode={geoLocationInfo.countryCode}
                  dataApiEndpoint="/api"
                  dehydratedState={oddsGrids[market.ocMarketId] as DehydratedState}
                  deviceType={deviceType}
                  gridStartsExpanded={deviceType === 'MOBILE'}
                  i18n={i18n}
                  marketId={market.ocMarketId}
                  marketIndex={idx}
                  marketName={market.marketDisplayName}
                  oddsFormat={oddsFormat}
                  // @ts-ignore
                  oddsShown={oddsShown}
                  overrideBookies={overrideBookies}
                  overrideMarketIds={overrideMarketIds}
                  repub={repub}
                  resourcesUrl={resourcesUrl}
                  subdivision={geoLocationInfo.subdivisionCode}
                  subeventType={market.subeventType}
                  tooltipForNewUser={idx === 0}
                />
                {idx === 0 ? renderGridOffers() : null}
              </Fragment>
            );
          })}
      </div>
    );
  };

  const renderActiveTabContent = () => {
    if (selectedTab.name === BEST_ODDS) {
      return (
        <>
          <Filters
            dataTestId="best-odds-tab-filters"
            handleMarketChange={handleMarketChange}
            isOC={isOC}
            marketGroupNames={marketGroupNames}
            selectedMarketGroup={selectedMarketGroup}
          />

          <div
            data-testid="best-odds-tab-subevent-markets"
            style={{
              width: '100%',
              margin: 'auto',
              maxWidth: 'var(--ogm-max-width)',
              padding: '0 16px 20px 16px',
            }}
          >
            {renderSubeventMarkets()}
          </div>
        </>
      );
    }

    if (selectedTab.name === OFFERS) {
      return tabbedOffers ? (
        <div
          data-testid="offers-tab-div"
          style={{
            width: '100%',
            padding: '0 16px',
          }}
        >
          <GridGroupOffers
            {...tabbedOffers}
            {...offersClickoutConfig}
            DISABLE_OFFER_WIDGETS={DISABLE_OFFER_WIDGETS}
            className="offersTab"
            dehydratedState={tabbedOffersData}
          />
        </div>
      ) : null;
    }
  };

  return (
    <>
      <Tabs handleTabChange={handleTabChange} isOC={isOC} selectedTab={selectedTab} tabElements={tabElements} />
      {renderActiveTabContent()}
    </>
  );
};
